import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { capitalize } from 'lodash';
import { CircularProgress, Tooltip, styled } from '@mui/material';
import { GridColumns, GridRowModel, GridOverlay, GridSortModel, GridActionsCellItem, GridRowParams } from '@mui/x-data-grid-pro';
import { TabContainer, TabContent, TabDataGridNoRowHover, TabToolbar } from '../../util/SharedStyles';
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import NoRecordsMessage from '../NoRecordsMessage';
import { ClientPortalUserModel, RequestResult, UpsertPortalUserInput, UserRole } from '../../gql-types.generated';
import { selectError, selectClientPortalUsers, captureDeleteClientPortalUserStatus, clearError, captureUpsertClientPortalUserStatus, selectUpsertClientPortalUserStatus, selectDeleteClientPortalUserStatus } from '../../features/ClientDetails/ClientDetailsSlice';
import { deleteClientPortalUser, fetchClientPortalUsers, upsertClientPortalUser } from '../../features/ClientDetails/ClientDetailsActions';
import PortalUserDialog from '../dialogs/PortalUserDialog';
import DeleteDialog from '../dialogs/DeleteDialog';
import { viewerIsAdmin } from '../../util/ViewerUtility';
import ErrorMessage from '../ErrorMessage';
import { setToastConfig } from '../../features/EDIContainer/EDIContainerSlice';
import { ToastSeverity } from '../../util/Constants';
import CreateNewButton from '../buttons/CreateNewButton';

const TabContainerWithMargin = styled(TabContainer)((props) => ({
    marginTop: '16px'
}));

interface PortalUserListProps {
    viewerRole: UserRole | undefined;
    tenantId: string;
}

const ClientPortalUserList: React.FC<PortalUserListProps> = props => {
    const { viewerRole, tenantId } = props;
    const dispatch = useAppDispatch();
    const [portalUserRows, setPortalUserRows] = useState<GridRowModel[]>([]);
    const [openModify, setOpenModify] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [deleteErrorMessage, setDeleteErrorMessage] = useState('');
    const [selectedUser, setSelectedUser] = useState<undefined | ClientPortalUserModel>(undefined);
    const [sortModel, setSortModel] = useState<GridSortModel>([
            {
                field: 'lastName',
                sort: 'asc',
            }
        ]);

    const isAdmin = false; //setting to false for now to hide action buttons. use this in future: viewerIsAdmin(viewerRole);

    const portalUsers = useAppSelector(selectClientPortalUsers);
    const upsertUserStatus = useAppSelector(selectUpsertClientPortalUserStatus);
    const deleteUserStatus = useAppSelector(selectDeleteClientPortalUserStatus);
        
    const error = useAppSelector(selectError);

    useEffect(() => {
        if (tenantId) {
            dispatch(fetchClientPortalUsers(tenantId));  
        }
    }, [tenantId]);

    useEffect(() => {
        if (upsertUserStatus?.result === RequestResult.Success) {
            dispatch(setToastConfig({
                message: upsertUserStatus.message as string,
                severity: ToastSeverity.Success
            }));
            // close the modify dialog and refresh list
            onPortalUserDialogClose();
            refreshList();
        }
    }, [upsertUserStatus?.result]);
        
    useEffect(() => {
        if (deleteUserStatus?.result === RequestResult.Success) {
            dispatch(setToastConfig({
                message: deleteUserStatus.message as string,
                severity: ToastSeverity.Success
            }));
            // close the delete dialog and refresh list
            onDeleteDialogClose();
            refreshList();
        }
        if (deleteUserStatus?.result === RequestResult.Fail) {
            setDeleteErrorMessage(deleteUserStatus.message as string);
        }
    }, [deleteUserStatus?.result]);

    useEffect(() => {
        setPortalUserRows(getPortalUserRows());
    }, [portalUsers]);

   const getSelectedRowUser = useCallback((rowId: string) => () => {
        if (rowId && portalUsers?.length) {
            let user = portalUsers.find(user => user.id === rowId);
            return user;
        }
        return undefined;
    }, [portalUsers]);
   
    const deleteProcessHandler = useCallback((rowId: string) => () => {
        let user = getSelectedRowUser(rowId);
        if (user) {
            setSelectedUser(user);
            dispatch(captureDeleteClientPortalUserStatus());
            setDeleteErrorMessage('');
            setOpenDelete(true);
        }
    }, [getSelectedRowUser, dispatch]);

    const editProcessHandler = useCallback((rowId: string) => () => {
        let user = getSelectedRowUser(rowId);
        if (user) {
            setSelectedUser(user);
            dispatch(clearError());
            setOpenModify(true);
        }
    }, [getSelectedRowUser, dispatch]);

    const getPortalUserRows = () => {
        if (portalUsers && portalUsers.length > 0) {
            return portalUsers.map((userRow: ClientPortalUserModel) => {
                const { id, firstName, lastName, email, customerRole, subscriptionRole, createdTime } = userRow;
                
                return {
                    _raw: userRow,
                    id,
                    firstName, 
                    lastName,
                    email, 
                    createdTime, 
                    customerRole,
                    subscriptionRole,
                } as GridRowModel;
            }) as GridRowModel[];
        } else {
            return [];
        }
    };

    const portalUsersColumns = useMemo<GridColumns<GridRowModel>>(
        () => [
            {
                field: 'firstName',
                headerName: 'FIRST NAME',
                minWidth: 100,
                flex: 2,
                sortable: true,
            }, {
                field: 'lastName',
                headerName: 'LAST NAME',
                minWidth: 100,
                flex: 2,
                sortable: true,
            }, {
                field: 'email',
                headerName: 'EMAIL',
                minWidth: 100,
                flex: 2,
                sortable: true,
            }, {
                field: 'customerRole',
                headerName: 'CUSTOMER ROLE',
                minWidth: 100,
                flex: 1,
                sortable: false,
                valueFormatter(params) {
                    return capitalize(params.value);
                },
            }, {
                field: 'subscriptionRole',
                headerName: 'SUBSCRIPTION ROLE',
                minWidth: 100,
                flex: 1,
                sortable: false,
                renderCell(params) {
                    return capitalize(params.value); 
                },
            }, {
                field: 'actions',
                type: 'actions',
                sortable: false,
                resizable: false,
                headerName: '',
                width: 100,
                headerAlign: 'right',
                align: 'center',
                hide: !isAdmin, // hide column if not admin role
                // eslint-disable-next-line react/display-name
                getActions: (params: GridRowParams<GridRowModel>) => [
                    <GridActionsCellItem
                        icon={<Tooltip title="Edit"><EditIcon /></Tooltip>}
                        label="Edit"
                        color="primary"
                        onClick={editProcessHandler(params.row.id)}
                        showInMenu={false}
                    />,
                    <GridActionsCellItem
                        icon={<Tooltip title="Delete"><DeleteIcon /></Tooltip>}
                        label="Delete"
                        color="error"
                        onClick={deleteProcessHandler(params.row.id)}
                        showInMenu={false}
                    />
                ],
            }
        ],
        [editProcessHandler, deleteProcessHandler, isAdmin],
    );
   
    const loadingOverlay = () => {
        return (
            <GridOverlay>
                {error && (
                    <ErrorMessage title='Unable to load the Portal Users' error={error}></ErrorMessage>
                )}
                {!error && !portalUsers && (
                    <CircularProgress aria-label={'progress spinner'} key={'logs-spinner'} size={42} sx={{ zIndex: 1 }} />
                )}
            </GridOverlay>
        );
    };
    
    const noRowsOverlay = () => {
        return (
            <GridOverlay>
                {error && (
                    <ErrorMessage title='Unable to load the Portal Users' error={error}></ErrorMessage>
                )}
                {!error && (portalUsers && portalUsers.length <= 0) && isAdmin && (
                    <NoRecordsMessage topMargin={6} actionButtonText="Add Portal User" actionButtonClick={onAddUserClick} />
                )}
                {!error && (portalUsers && portalUsers.length <= 0) && !isAdmin && (
                    <NoRecordsMessage topMargin={6} />
                )}
            </GridOverlay>
        );
    };
    

    const onSortModelChange = (model: GridSortModel) => {
        setSortModel(model);
    };

    const refreshList = () => {
        dispatch(fetchClientPortalUsers(tenantId));
    };

    const onAddUserClick = () => {
        // Clear error 
        dispatch(clearError());
        // ensure no previously selected user is set
        if (selectedUser) {
            setSelectedUser(undefined);
        }
        setOpenModify(true);
    };

    const onDialogClose = () => {
        // Clear error and selectedUser on close.
        dispatch(clearError());
        setSelectedUser(undefined);
    };

    const onPortalUserDialogClose = () => {
        setOpenModify(false);
        onDialogClose();
        // clear upsert status
        dispatch(captureUpsertClientPortalUserStatus());
    }
    
    const onPortalUserDialogSave = (
        input: UpsertPortalUserInput,
    ) => {
        if (input) {
            dispatch(upsertClientPortalUser(input));
        }
    }

    const onDeleteDialogClose = () => {
        setOpenDelete(false);
        onDialogClose();
        setDeleteErrorMessage('');
        // clear delete status
        dispatch(captureDeleteClientPortalUserStatus());
    };

    const onDeleteDialogConfirm = (rowId: string) => {
        dispatch(deleteClientPortalUser(tenantId, rowId));
    };


    return (
        <TabContainerWithMargin>
            <TabToolbar justify={"flex-end"}>
                {isAdmin &&
                <CreateNewButton
                    text="Add Portal User"
                    onClick={onAddUserClick}
                    data-cy="add-new-user"
                />}
            </TabToolbar>
            <TabContent>
                <TabDataGridNoRowHover
                    loading={portalUsers === undefined}
                    headerHeight={38}
                    rowHeight={52}
                    aria-label="Client Portal User List"
                    hideFooter
                    disableColumnMenu
                    disableColumnFilter
                    disableSelectionOnClick
                    rows={portalUserRows}
                    columns={portalUsersColumns}
                    sortingOrder={['asc', 'desc']}
                    sortModel={sortModel}
                    onSortModelChange={onSortModelChange}
                    components={{
                        // eslint-disable-next-line react/display-name,@typescript-eslint/naming-convention
                        LoadingOverlay: loadingOverlay,
                        // eslint-disable-next-line react/display-name,@typescript-eslint/naming-convention
                        NoRowsOverlay: noRowsOverlay,
                    }}
                />
            </TabContent>
            <PortalUserDialog
                isOpen={openModify}
                tenantId={tenantId}
                user={selectedUser}
                onClose={onPortalUserDialogClose}
                onSave={onPortalUserDialogSave}
                error={error}
            />
            <DeleteDialog
                isOpen={openDelete}
                heading={'Delete Portal User'}
                message={`Are you sure you want to delete '${selectedUser?.email}' from this Client?`}
                id={selectedUser?.id as string}
                onConfirm={onDeleteDialogConfirm}
                onReject={onDeleteDialogClose}
                errorMessage={deleteErrorMessage}
            />
        </TabContainerWithMargin>
    );
}

export default ClientPortalUserList;